import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Header } from '@setvi/shared/components/sdrawer-content/components/header';
import { DrawerContent } from '@setvi/shared/enums/common';
import { SSearchInput } from '@setvi/shared/components/sforms/ssearch';
import { TContact } from '@setvi/shared/components/sdrawer-content/components/contact';
import {
  Box,
  Checkbox,
  CircularProgress,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core';
import { SInfiniteScroll, SEmptyState } from '@setvi/shared/components';
import { AxiosMethods, GroupsApi } from '../../../../enums';
import { axiosHelper } from '../../../../services/axios-instance';
import { ContactsQueryKey } from '../../../../services/react-query/query/contacts';
import { Subheader } from '../../components/subheader';
import { useGroups } from './useGroups';
import { Group } from './group';
import { GroupsContacts } from './groups-contacts';
import { Recipient } from '../../../../interfaces';

interface GroupsProps {
  selectedContacts?: Recipient[];
  modifyRecipientList: (contact: TContact) => void;
  modifyRecipientListWithListOfContacts: (
    contacts: TContact[],
    add: boolean
  ) => void;
  setShowContent: (content: DrawerContent | null) => void;
  handleClose: () => void;
}

const useGroupsStyles = makeStyles(() => ({
  loader: {
    textAlign: 'center'
  }
}));

const fetchAllGroupContacts = (groupId: number) =>
  axiosHelper({
    endpoint: GroupsApi.GroupContacts.replace(':groupId', groupId.toString()),
    method: AxiosMethods.GET,
    params: { pageNumber: 1, pageSize: 1000 },
    baseURL: process.env.ADMIN_API_BASE_URL
  }).then(res => (res?.Data?.Items || []) as TContact[]);

export const Groups = ({
  selectedContacts,
  modifyRecipientList,
  modifyRecipientListWithListOfContacts,
  setShowContent,
  handleClose
}: GroupsProps) => {
  const styles = useGroupsStyles();
  const queryClient = useQueryClient();
  const [contactsCache, setContactsCache] = useState<
    Record<number, TContact[]>
  >({});
  const [loadingGroupId, setLoadingGroupId] = useState<number | null>(null);
  const {
    groups,
    isLoading,
    fetchMoreGroups,
    searchString,
    setSearchString,
    setSelectedGroup,
    selectedGroup
  } = useGroups();

  const getGroupContacts = useCallback(
    async (groupId: number): Promise<TContact[]> => {
      if (contactsCache[groupId]) return contactsCache[groupId];

      const contacts = await queryClient.fetchQuery({
        queryKey: [ContactsQueryKey.GROUP_CONTACTS, groupId, 'drawer-all'],
        queryFn: () => fetchAllGroupContacts(groupId)
      });

      setContactsCache(prev => ({ ...prev, [groupId]: contacts }));
      return contacts;
    },
    [contactsCache, queryClient]
  );

  const getCheckedState = (groupId: number) => {
    const cached = contactsCache[groupId];
    if (!cached || cached.length === 0) return false;
    return cached.every(contact =>
      selectedContacts?.some(
        selected => selected.Email.toLowerCase() === contact.Email.toLowerCase()
      )
    );
  };

  if (selectedGroup)
    return (
      <GroupsContacts
        selectedGroup={selectedGroup}
        modifyRecipientList={modifyRecipientList}
        handleBack={() => setSelectedGroup(null)}
        handleClose={handleClose}
        selectedContacts={selectedContacts}
      />
    );

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Header title="Recipients" handleClose={handleClose} />
      <Subheader
        handleBack={() => setShowContent(DrawerContent.contactsNavigation)}
        text="Groups"
      />
      <Box p={2} mb={1}>
        <SSearchInput
          defaultValue={searchString}
          onChange={value => setSearchString(value)}
        />
      </Box>
      <Box overflow="auto">
        <SInfiniteScroll
          list={groups}
          keyGetter={group => group.Id}
          fetchMore={fetchMoreGroups}
          render={group => (
            <Group
              group={group}
              onGroupClick={() => {
                setSearchString('');
                setSelectedGroup(group);
              }}
              checkbox={
                <Tooltip
                  disableHoverListener={group.ContactCount > 0}
                  title="Group does not have any contacts"
                  arrow>
                  <span>
                    {loadingGroupId === group.Id ? (
                      <Box display="flex" p={1.25}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <Checkbox
                        size="small"
                        checked={getCheckedState(group.Id)}
                        onChange={async ({ target: { checked } }) => {
                          setLoadingGroupId(group.Id);
                          try {
                            const contacts = await getGroupContacts(group.Id);
                            modifyRecipientListWithListOfContacts(
                              contacts,
                              checked
                            );
                          } finally {
                            setLoadingGroupId(null);
                          }
                        }}
                        color="primary"
                        disabled={group.ContactCount === 0}
                      />
                    )}
                  </span>
                </Tooltip>
              }
            />
          )}>
          <>
            {isLoading && (
              <Typography className={styles.loader} variant="h6">
                Loading...
              </Typography>
            )}
            {!isLoading && groups?.length === 0 && (
              <SEmptyState
                imageSrc="/images/no-groups.png"
                subTitle="No Groups Available"
              />
            )}
          </>
        </SInfiniteScroll>
      </Box>
    </Box>
  );
};
